import { styled } from "@linaria/react"
import { useCallback, useEffect, useRef } from "react"

import type { RefObject } from "react"

export const SelfIntersectionObserver = (
  {
    onBecomeVisible,
    onBecomeHidden,
    callback,
    threshold,
    rootMargin,
    externalRef
  }: {
  onBecomeVisible?: () => void
  onBecomeHidden?: () => void
  callback?: (entries: IntersectionObserverEntry[]) => void
  threshold?: number[]
  rootMargin?: string
  externalRef?: RefObject<Element | null>
}) => {
  const observedElementRef = useRef<Element | null>(null)
  const observerRef = useRef<IntersectionObserver>(null)

  const handleIntersect = useCallback((entries: IntersectionObserverEntry[]) => {
    callback?.(entries)
    if (entries[entries.length - 1].isIntersecting) {
      onBecomeVisible?.()
    } else {
      onBecomeHidden?.()
    }
  }, [callback, onBecomeVisible, onBecomeHidden])

  useEffect(() => {
    const element = externalRef?.current || observedElementRef.current
    if (!element) return

    observerRef.current = new IntersectionObserver(handleIntersect, {
      root: null,
      rootMargin: rootMargin,
      threshold: threshold,
    })

    observerRef.current.observe(element)

    return () => {
      observerRef.current?.disconnect()
    }
  }, [externalRef, handleIntersect, rootMargin, threshold])

  const setRef = useCallback((node: Element | null) => {
    if (externalRef) {
      externalRef.current = node
    } else {
      observedElementRef.current = node
    }
  }, [externalRef])

  return (
    <Observable ref={setRef} />
  )
}

const Observable = styled.div`
`